import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import { useAuditUserId } from '../auth/AuthContext';
import { supabase } from '../config/supabase';
import { downloadBlob } from '../services/zipExport';
import type { LookupState } from '../types';
import type { PlayerRatingBasis } from '../types';
import { lineupTierKey } from '../utils/lineupPlayerSort';
import {
  assertValidPracticeRotationPlan,
  defaultPracticeRotationSettings,
  displayPracticeCourtLabel,
  generatePracticeRotationPlan,
  practiceRotationFilename,
  practiceRotationPlanToText,
  practiceRsvpSnapshotDifferences,
  practiceRsvpSnapshotHash,
  practiceStrengthMixDescriptions,
  renderPracticeRotationPdf,
  resizePracticeCourtLabels,
  toPracticeGender,
  validatePracticeRotationPlan,
  type PracticePartnerPreference,
  type PracticeRotationPairRule,
  type PracticeRotationParticipant,
  type PracticeRotationPlan,
  type PracticeRotationSettings,
} from '../utils/practiceRotationPlanner';
import { usePersistentScrollRestoration } from '../hooks/usePersistentViewState';
import { summarizeGroupMeOnlyRsvps } from '../utils/groupmeOnlyRsvp';
import { buildBreadcrumbTrail, practiceRotationCanonicalContext } from '../utils/uxNavigation';
import { summarizeEventGuests } from '../utils/eventGuestAttendance';
import { BreadcrumbTrail } from './ux/BreadcrumbTrail';

export type PracticeTeamEvent = {
  id: string;
  team_id: string | null;
  title: string;
  event_date: string | null;
  workspace_id?: string | null;
  audience_scope?: 'workspace' | 'selected_teams';
  groupme_only_rsvp_event_id?: string | null;
};

export type PracticeAttendanceParticipant = {
  id: string;
  display_name: string | null;
  groupme_display_name?: string | null;
  rsvp_status: string;
  guest_count?: number;
  match_status: string;
  player_id: string | null;
  players?: {
    id?: string;
    first_name: string;
    last_name: string;
    sex: string | null;
    estimated_dynamic_rating?: number | null;
    utr_rating?: number | null;
    utr_doubles_rating?: number | null;
    utr_singles_rating?: number | null;
  } | null;
};

type PracticeRotationPlanRow = {
  id: string;
  revision: number;
  generated_at: string;
  settings_snapshot: PracticeRotationSettings;
  participant_snapshot: PracticeRotationParticipant[];
  rsvp_snapshot_hash: string;
  rounds: PracticeRotationPlan['rounds'];
  metrics: PracticeRotationPlan['metrics'];
  warnings: string[];
};

type PracticeRotationConfigRow = {
  settings: Partial<PracticeRotationSettings>;
};

type PracticeManualRsvpStatus = 'Going' | 'Not Going' | 'Maybe';
type PracticeManualRsvpDraft = { playerId: string; status: PracticeManualRsvpStatus; note: string };
type SnackSetter = (snack: { severity: 'success' | 'warning' | 'error'; message: string } | null) => void;

function lookupLabel(lookups: LookupState, key: keyof LookupState, value: string | null | undefined): string {
  if (!value) return '';
  return lookups[key]?.find((item) => item.value === value)?.label || '';
}

function playerName(participant: PracticeAttendanceParticipant): string {
  return participant.players
    ? `${participant.players.first_name} ${participant.players.last_name}`
    : participant.display_name || participant.groupme_display_name || 'Unnamed attendee';
}

function numericRating(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function tierRating(value: string | null | undefined): number | null {
  const key = lineupTierKey(value);
  if (!key) return null;
  return 5 - Number(key.slice(-1));
}

function playerPracticeRanking(participant: PracticeAttendanceParticipant, ratingBasis: PlayerRatingBasis, rosterTier: string | null | undefined): number | null {
  if (ratingBasis === 'tier') return tierRating(rosterTier);
  if (ratingBasis === 'utr') return numericRating(participant.players?.utr_doubles_rating ?? participant.players?.utr_rating ?? participant.players?.utr_singles_rating);
  return numericRating(participant.players?.estimated_dynamic_rating);
}

function participantToRotationParticipant(participant: PracticeAttendanceParticipant, ratingBasis: PlayerRatingBasis, rosterTier: string | null | undefined): PracticeRotationParticipant {
  return {
    id: participant.id,
    playerId: participant.player_id || participant.players?.id || null,
    name: playerName(participant),
    gender: toPracticeGender(participant.players?.sex),
    groupMeName: participant.groupme_display_name,
    matched: participant.match_status === 'matched' && Boolean(participant.player_id || participant.players?.id),
    ranking: playerPracticeRanking(participant, ratingBasis, rosterTier),
  };
}

function planFromRow(row: PracticeRotationPlanRow, event: PracticeTeamEvent, teamName: string | null): PracticeRotationPlan {
  return {
    algorithmVersion: 'practice_rotation_v1',
    generatedAt: row.generated_at,
    eventName: event.title,
    eventDate: event.event_date,
    teamName,
    participantSnapshot: row.participant_snapshot || [],
    rsvpSnapshotHash: row.rsvp_snapshot_hash,
    settingsSnapshot: row.settings_snapshot,
    rounds: row.rounds || [],
    metrics: row.metrics,
    warnings: row.warnings || [],
  };
}

function formatGeneratedAt(value: string | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function teamText(team: PracticeRotationPlan['rounds'][number]['courts'][number]['teamA']): string {
  if (!team) return 'Open';
  const text = team.players.map((player) => player.name).join(' / ');
  const marker = team.source === 'upcoming_match' ? ' ★' : '';
  return `${text}${marker}`;
}

function rsvpChipSx(kind: 'men' | 'women' | 'unmatched') {
  if (kind === 'men') {
    return {
      bgcolor: 'rgba(25, 118, 210, 0.08)',
      borderColor: 'rgba(25, 118, 210, 0.45)',
      color: '#0d47a1',
      fontWeight: 700,
    };
  }
  if (kind === 'women') {
    return {
      bgcolor: 'rgba(194, 24, 91, 0.08)',
      borderColor: 'rgba(194, 24, 91, 0.45)',
      color: '#880e4f',
      fontWeight: 700,
    };
  }
  return {
    bgcolor: 'rgba(97, 97, 97, 0.08)',
    borderColor: 'rgba(97, 97, 97, 0.4)',
    color: '#212121',
    fontWeight: 700,
  };
}

function rsvpSummaryChipSx(kind: 'going' | 'maybe' | 'notGoing' | 'notResponded' | 'unmatched') {
  if (kind === 'going') return { bgcolor: 'success.main', color: '#fff', fontWeight: 800 };
  if (kind === 'maybe') return { bgcolor: '#fff3cd', color: '#5f4600', fontWeight: 800 };
  if (kind === 'notGoing') return { bgcolor: '#d32f2f', color: '#fff', fontWeight: 800 };
  if (kind === 'notResponded') return { bgcolor: '#212121', color: '#fff', fontWeight: 800 };
  return { bgcolor: 'rgba(97, 97, 97, 0.14)', color: '#212121', fontWeight: 800 };
}

function notRespondedCount(teamPlayerCount: number, rsvpSummary: ReturnType<typeof summarizeGroupMeOnlyRsvps>): number {
  return Math.max(0, teamPlayerCount - rsvpSummary.goingTotal - rsvpSummary.maybeTotal - rsvpSummary.notGoingTotal);
}

function groupPracticeParticipants(participants: PracticeRotationParticipant[]) {
  return participants.reduce<{ men: PracticeRotationParticipant[]; women: PracticeRotationParticipant[]; unmatched: PracticeRotationParticipant[] }>(
    (groups, participant) => {
      if (!participant.matched || participant.gender === 'unspecified') groups.unmatched.push(participant);
      else if (participant.gender === 'male') groups.men.push(participant);
      else if (participant.gender === 'female') groups.women.push(participant);
      else groups.unmatched.push(participant);
      return groups;
    },
    { men: [], women: [], unmatched: [] },
  );
}

function mergeSettings(base: Partial<PracticeRotationSettings> | null | undefined): PracticeRotationSettings {
  const courtCount = Number(base?.courts || defaultPracticeRotationSettings.courts);
  return {
    ...defaultPracticeRotationSettings,
    ...(base || {}),
    courtLabels: resizePracticeCourtLabels(base?.courtLabels, courtCount),
    strengthMix: base?.strengthMix || defaultPracticeRotationSettings.strengthMix,
    weights: { ...defaultPracticeRotationSettings.weights, ...(base?.weights || {}) },
    sitFirstPlayerIds: base?.sitFirstPlayerIds || [],
    noSitPlayerIds: base?.noSitPlayerIds || [],
    sitLastPlayerIds: base?.sitLastPlayerIds || [],
    arriveLate: base?.arriveLate || [],
    leaveEarly: base?.leaveEarly || [],
    mustPartner: base?.mustPartner || [],
    avoidPartner: base?.avoidPartner || [],
  };
}

export function PracticeRotationPlanner({
  event,
  participants,
  teamPlayerCount,
  lookups,
  readOnly,
  manualRsvpDraft,
  onManualRsvpDraftChange,
  onSaveRsvp,
  onReplayRsvps,
  replayRsvpsDisabled,
  onBack,
  setSnack,
}: {
  event: PracticeTeamEvent;
  participants: PracticeAttendanceParticipant[];
  teamPlayerCount: number;
  lookups: LookupState;
  readOnly: boolean;
  manualRsvpDraft?: PracticeManualRsvpDraft;
  onManualRsvpDraftChange?: (patch: Partial<PracticeManualRsvpDraft>) => void;
  onSaveRsvp?: () => void;
  onReplayRsvps?: () => void;
  replayRsvpsDisabled?: boolean;
  onBack: () => void;
  setSnack: SnackSetter;
}) {
  const auditUserId = useAuditUserId();
  const teamName = lookupLabel(lookups, 'teams', event.team_id) || null;
  const team = lookups.teams.find((teamOption) => teamOption.value === event.team_id);
  const teamType = team?.team_gender_type || null;
  const teamRatingBasis = (team?.player_rating_basis || 'edr') as PlayerRatingBasis;
  const [rosterTiersByPlayerId, setRosterTiersByPlayerId] = useState<Record<string, string | null>>({});
  const goingParticipants = useMemo(
    () => participants
      .filter((participant) => participant.rsvp_status === 'Going')
      .map((participant) => participantToRotationParticipant(participant, teamRatingBasis, rosterTiersByPlayerId[participant.player_id || participant.players?.id || ''])),
    [participants, rosterTiersByPlayerId, teamRatingBasis],
  );
  const matchedPlayers = useMemo(
    () => goingParticipants.filter((participant) => participant.playerId),
    [goingParticipants],
  );
  const goingParticipantGroups = useMemo(() => groupPracticeParticipants(goingParticipants), [goingParticipants]);
  const rsvpSummary = useMemo(
    () => summarizeGroupMeOnlyRsvps(participants.map((participant) => ({
      latest_rsvp_status: participant.rsvp_status,
      match_status: participant.match_status,
      players: participant.players,
    }))),
    [participants],
  );
  const guestSummary = useMemo(() => summarizeEventGuests(participants), [participants]);
  const currentSnapshotHash = useMemo(() => practiceRsvpSnapshotHash(goingParticipants), [goingParticipants]);
  const [settings, setSettings] = useState<PracticeRotationSettings>(() => ({
    ...defaultPracticeRotationSettings,
    preferMixedTeams: teamType === 'mixed',
  }));
  const [activePlanRow, setActivePlanRow] = useState<PracticeRotationPlanRow | null>(null);
  const [upcomingPairs, setUpcomingPairs] = useState<PracticeRotationPairRule[]>([]);
  const [partnerPreferences, setPartnerPreferences] = useState<PracticePartnerPreference[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  usePersistentScrollRestoration({ userId: auditUserId, routeKey: 'practice-rotation', contextKey: event.id, ready: !loading && !busy });

  const activePlanCandidate = activePlanRow ? planFromRow(activePlanRow, event, teamName) : null;
  const activePlanValidation = activePlanCandidate ? validatePracticeRotationPlan(activePlanCandidate) : { valid: true, errors: [] };
  const activePlan = activePlanCandidate && activePlanValidation.valid ? activePlanCandidate : null;
  const staleDifferences = activePlan && activePlan.rsvpSnapshotHash !== currentSnapshotHash
    ? practiceRsvpSnapshotDifferences(activePlan.participantSnapshot, goingParticipants)
    : [];

  const load = useCallback(async () => {
    setLoading(true);
    const [configResult, planResult, preferenceResult] = await Promise.all([
      event.workspace_id
        ? supabase.from('practice_rotation_configs').select('settings').eq('workspace_id', event.workspace_id).maybeSingle()
        : Promise.resolve({ data: null, error: null }),
      supabase
        .from('practice_rotation_plans')
        .select('id, revision, generated_at, settings_snapshot, participant_snapshot, rsvp_snapshot_hash, rounds, metrics, warnings')
        .eq('event_id', event.id)
        .eq('is_active', true)
        .order('generated_at', { ascending: false })
        .limit(1)
        .maybeSingle(),
      event.workspace_id
        ? supabase
            .from('partner_preferences')
            .select('player1_id, player2_id, compatibility, pairing_rule')
            .eq('workspace_id', event.workspace_id)
            .eq('is_active', true)
            .is('deleted_at', null)
        : Promise.resolve({ data: [], error: null }),
    ]);

    const audienceTeamsResult = event.audience_scope === 'workspace' && event.workspace_id
      ? await supabase.from('teams').select('id').eq('workspace_id', event.workspace_id).eq('is_active', true).is('deleted_at', null)
      : await supabase.from('event_teams').select('team_id').eq('event_id', event.id);
    const audienceTeamIds = event.audience_scope === 'workspace'
      ? (audienceTeamsResult.data || []).map((team) => 'id' in team ? team.id : '')
      : (audienceTeamsResult.data || []).map((association) => 'team_id' in association ? association.team_id : '');
    const rosterResult = audienceTeamIds.length > 0
      ? await supabase
          .from('team_rosters')
          .select('player_id, player_tier')
          .in('team_id', audienceTeamIds.filter(Boolean))
          .eq('is_active', true)
          .neq('roster_status', 'inactive')
          .is('deleted_at', null)
      : event.team_id
      ? await supabase
          .from('team_rosters')
          .select('player_id, player_tier')
          .eq('team_id', event.team_id)
          .eq('is_active', true)
          .neq('roster_status', 'inactive')
          .is('deleted_at', null)
      : { data: [], error: null };

    const matchPairResult = event.team_id && event.audience_scope !== 'workspace'
      ? await supabase
          .from('lineup_entries')
          .select('player_id, match_event_id, league_configuration_positions(line_type), match_events!inner(team_id, match_date, match_status, lineup_status, deleted_at)')
          .eq('match_events.team_id', event.team_id)
          .gte('match_events.match_date', new Date().toISOString().slice(0, 10))
          .neq('match_events.match_status', 'Cancelled')
          .is('match_events.deleted_at', null)
          .is('deleted_at', null)
          .limit(300)
      : { data: [], error: null };

    setLoading(false);
    const toleratedMissing = (message: string | undefined) => /practice_rotation_|Could not find the table/i.test(message || '');
    if ((configResult.error && !toleratedMissing(configResult.error.message)) || (planResult.error && !toleratedMissing(planResult.error.message)) || preferenceResult.error || audienceTeamsResult.error || rosterResult.error || matchPairResult.error) {
      setSnack({
        severity: 'error',
        message: configResult.error?.message || planResult.error?.message || preferenceResult.error?.message || audienceTeamsResult.error?.message || rosterResult.error?.message || matchPairResult.error?.message || 'Unable to load practice rotation data.',
      });
      return;
    }

    const config = configResult.data as PracticeRotationConfigRow | null;
    const loadedPlan = (planResult.data || null) as unknown as PracticeRotationPlanRow | null;
    const nextSettings = mergeSettings(loadedPlan?.settings_snapshot || config?.settings || {
      ...defaultPracticeRotationSettings,
      preferMixedTeams: teamType === 'mixed',
    });
    setSettings(nextSettings);
    setActivePlanRow(loadedPlan);
    setPartnerPreferences(((preferenceResult.data || []) as Array<{ player1_id: string; player2_id: string; compatibility: string | null; pairing_rule: string | null }>).map((row) => ({
      player1Id: row.player1_id,
      player2Id: row.player2_id,
      compatibility: row.compatibility,
      pairingRule: row.pairing_rule,
    })));
    setRosterTiersByPlayerId(Object.fromEntries(((rosterResult.data || []) as Array<{ player_id: string; player_tier: string | null }>).map((row) => [row.player_id, row.player_tier])));

    const byMatch = new Map<string, string[]>();
    ((matchPairResult.data || []) as unknown as Array<{ player_id: string; match_event_id: string; league_configuration_positions?: { line_type?: string | null } | Array<{ line_type?: string | null }> }>).forEach((row) => {
      const position = Array.isArray(row.league_configuration_positions) ? row.league_configuration_positions[0] : row.league_configuration_positions;
      if (position?.line_type !== 'doubles') return;
      const values = byMatch.get(row.match_event_id) || [];
      values.push(row.player_id);
      byMatch.set(row.match_event_id, values);
    });
    const pairs: PracticeRotationPairRule[] = [];
    byMatch.forEach((playerIds) => {
      if (playerIds.length === 2) pairs.push({ player1Id: playerIds[0], player2Id: playerIds[1] });
    });
    setUpcomingPairs(pairs);
  }, [event.audience_scope, event.id, event.team_id, event.workspace_id, setSnack, teamType]);

  useEffect(() => {
    void load();
  }, [load]);

  const updateSettings = (patch: Partial<PracticeRotationSettings>) => {
    setSettings((current) => ({ ...current, ...patch }));
  };

  const updateCourtCount = (courtCount: number) => {
    setSettings((current) => {
      const courts = Math.min(10, Math.max(1, Number(courtCount || defaultPracticeRotationSettings.courts)));
      return { ...current, courts, courtLabels: resizePracticeCourtLabels(current.courtLabels, courts) };
    });
  };

  const updateCourtLabel = (index: number, label: string) => {
    setSettings((current) => ({
      ...current,
      courtLabels: resizePracticeCourtLabels(current.courtLabels, current.courts).map((currentLabel, currentIndex) => (
        currentIndex === index ? label : currentLabel
      )),
    }));
  };

  const savePlan = async (plan: PracticeRotationPlan) => {
    if (readOnly) return;
    try {
      assertValidPracticeRotationPlan(plan);
    } catch (error) {
      console.error('Practice rotation validation failed before save', error);
      setSnack({ severity: 'error', message: "CourtCaptain couldn't produce a valid rotation. Please regenerate the plan." });
      return;
    }
    setBusy(true);
    if (!event.workspace_id) {
      setBusy(false);
      setSnack({ severity: 'error', message: 'This Team Event is missing workspace context. Refresh Team Events and try again.' });
      return;
    }
    const currentRevision = (activePlanRow?.revision || 0) + 1;
      const deactivate = await supabase
      .from('practice_rotation_plans')
      .update({ is_active: false, updated_by: auditUserId })
      .eq('event_id', event.id)
      .eq('is_active', true);
    if (deactivate.error) {
      setBusy(false);
      setSnack({ severity: 'error', message: deactivate.error.message });
      return;
    }
    const insert = await supabase
      .from('practice_rotation_plans')
      .insert({
        event_id: event.id,
        groupme_only_rsvp_event_id: event.groupme_only_rsvp_event_id || null,
        team_id: event.team_id,
        workspace_id: event.workspace_id,
        revision: currentRevision,
        generated_by: auditUserId,
        algorithm_version: plan.algorithmVersion,
        settings_snapshot: plan.settingsSnapshot,
        participant_snapshot: plan.participantSnapshot,
        rsvp_snapshot_hash: plan.rsvpSnapshotHash,
        rounds: plan.rounds,
        metrics: plan.metrics,
        warnings: plan.warnings,
        created_by: auditUserId,
        updated_by: auditUserId,
      })
      .select('id, revision, generated_at, settings_snapshot, participant_snapshot, rsvp_snapshot_hash, rounds, metrics, warnings')
      .single();
    setBusy(false);
    if (insert.error) {
      setSnack({ severity: 'error', message: insert.error.message.includes('practice_rotation') ? 'Apply the Practice Rotation Planner migration in this environment before saving plans.' : insert.error.message });
      return;
    }
    setActivePlanRow(insert.data as unknown as PracticeRotationPlanRow);
    setSnack({ severity: 'success', message: 'Practice rotation saved.' });
  };

  const generate = async () => {
    if (goingParticipants.length === 0) {
      setSnack({ severity: 'warning', message: 'No attendees are marked Going for this event.' });
      return;
    }
    try {
      const plan = generatePracticeRotationPlan({
        participants: goingParticipants,
        settings,
        upcomingMatchPairs: upcomingPairs,
        partnerPreferences,
        eventName: event.title,
        eventDate: event.event_date,
        teamName,
      });
      await savePlan(plan);
    } catch (error) {
      console.error('Practice rotation generation failed validation', error);
      setSnack({ severity: 'error', message: "CourtCaptain couldn't produce a valid rotation. Please regenerate the plan." });
    }
  };

  const copyPlan = async () => {
    if (!activePlan) return;
    try {
      await navigator.clipboard.writeText(practiceRotationPlanToText(activePlan));
      setSnack({ severity: 'success', message: 'Practice rotation copied.' });
    } catch (error) {
      console.error('Practice rotation validation failed before copy', error);
      setSnack({ severity: 'error', message: "CourtCaptain couldn't produce a valid rotation. Please regenerate the plan." });
    }
  };

  const downloadPdf = async () => {
    if (!activePlan) return;
    try {
      const blob = await renderPracticeRotationPdf(activePlan);
      downloadBlob(blob, practiceRotationFilename(activePlan));
      setSnack({ severity: 'success', message: 'Practice rotation PDF generated.' });
    } catch (error) {
      console.error('Practice rotation validation failed before PDF export', error);
      setSnack({ severity: 'error', message: "CourtCaptain couldn't produce a valid rotation. Please regenerate the plan." });
    }
  };

  const addPairRule = (key: 'mustPartner' | 'avoidPartner') => {
    const ids = matchedPlayers.slice(0, 2).map((participant) => participant.playerId).filter(Boolean) as string[];
    if (ids.length < 2) return;
    updateSettings({ [key]: [...settings[key], { player1Id: ids[0], player2Id: ids[1] }] } as Partial<PracticeRotationSettings>);
  };

  const renderGoingChips = (rows: PracticeRotationParticipant[], kind: 'men' | 'women' | 'unmatched') => (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
      {rows.map((participant) => (
        <Chip
          key={`${participant.id}:${participant.playerId || participant.name}`}
          size="small"
          variant="outlined"
          label={`${participant.name}${kind === 'unmatched' && participant.matched ? ' (Unspecified)' : ''}`}
          sx={rsvpChipSx(kind)}
        />
      ))}
    </Stack>
  );

  return (
    <Stack spacing={2}>
      <BreadcrumbTrail items={buildBreadcrumbTrail(practiceRotationCanonicalContext(event.title, `/team-events/${event.id}`))} />
      <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ alignSelf: 'flex-start' }}>Back to Practice</Button>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1}>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>{event.title}</Typography>
          <Typography variant="body2" color="text.secondary">
            {teamName || 'Unassigned team'} · {event.event_date || 'No event date'}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {onReplayRsvps && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<RefreshIcon />}
                disabled={readOnly || replayRsvpsDisabled}
                onClick={onReplayRsvps}
                sx={{ alignSelf: 'flex-start' }}
              >
                Replay RSVPs
              </Button>
            )}
          </Stack>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Event RSVPs</Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            <Chip size="small" label={`${rsvpSummary.goingTotal} Going`} sx={rsvpSummaryChipSx('going')} />
            <Chip size="small" label={`${guestSummary.guests} Guests`} variant="outlined" color="success" />
            <Chip size="small" label={`${guestSummary.totalAttending} Total Attending`} variant="outlined" color="success" />
            <Chip size="small" label={`${rsvpSummary.maybeTotal} Maybe`} sx={rsvpSummaryChipSx('maybe')} />
            <Chip size="small" label={`${rsvpSummary.notGoingTotal} Not Going`} sx={rsvpSummaryChipSx('notGoing')} />
            <Chip size="small" label={`${notRespondedCount(teamPlayerCount, rsvpSummary)} Not Responded`} sx={rsvpSummaryChipSx('notResponded')} />
            <Chip size="small" label={`${goingParticipantGroups.unmatched.length} Unmatched`} sx={rsvpSummaryChipSx('unmatched')} />
          </Stack>
          {goingParticipants.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No players are currently marked Going.
            </Typography>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
              {goingParticipantGroups.men.length > 0 && (
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 800, mb: 0.75 }}>Men ({goingParticipantGroups.men.length})</Typography>
                  {renderGoingChips(goingParticipantGroups.men, 'men')}
                </Box>
              )}
              {goingParticipantGroups.women.length > 0 && (
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 800, mb: 0.75 }}>Women ({goingParticipantGroups.women.length})</Typography>
                  {renderGoingChips(goingParticipantGroups.women, 'women')}
                </Box>
              )}
              {goingParticipantGroups.unmatched.length > 0 && (
                <Box sx={{ gridColumn: { lg: goingParticipantGroups.men.length || goingParticipantGroups.women.length ? '1 / -1' : 'auto' } }}>
                  <Typography variant="body2" sx={{ fontWeight: 800, mb: 0.75 }}>Unmatched ({goingParticipantGroups.unmatched.length})</Typography>
                  {renderGoingChips(goingParticipantGroups.unmatched, 'unmatched')}
                </Box>
              )}
            </Box>
          )}
          {guestSummary.goingGuestEntries.length > 0 && (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 800, mb: 0.75 }}>
                Guests ({guestSummary.goingGuestEntries.length}) · Informational only
              </Typography>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {guestSummary.goingGuestEntries.map((guest) => (
                  <Chip key={guest.id} size="small" variant="outlined" label={guest.name} />
                ))}
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                Guests count toward physical attendance but are not eligible for automatic rotation assignments.
              </Typography>
            </Box>
          )}
          {!readOnly && manualRsvpDraft && onManualRsvpDraftChange && onSaveRsvp && (
            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'rgba(255, 255, 255, 0.72)' }}>
              <Stack spacing={1}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Add/Change RSVPs</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(220px, 1fr) minmax(150px, 0.35fr) minmax(220px, 1fr) auto' }, gap: 1, alignItems: 'center' }}>
                  <FormControl size="small">
                    <InputLabel>Player</InputLabel>
                    <Select
                      label="Player"
                      value={manualRsvpDraft.playerId}
                      onChange={(change) => onManualRsvpDraftChange({ playerId: change.target.value })}
                    >
                      <MenuItem value="">Select player</MenuItem>
                      {lookups.players.map((player) => <MenuItem key={player.value} value={player.value}>{player.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      label="Status"
                      value={manualRsvpDraft.status}
                      onChange={(change) => onManualRsvpDraftChange({ status: change.target.value as PracticeManualRsvpStatus })}
                    >
                      <MenuItem value="Going">Going</MenuItem>
                      <MenuItem value="Maybe">Maybe</MenuItem>
                      <MenuItem value="Not Going">Not Going</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    label="Note"
                    value={manualRsvpDraft.note}
                    onChange={(change) => onManualRsvpDraftChange({ note: change.target.value })}
                    placeholder="Optional"
                  />
                  <Button variant="outlined" startIcon={<SaveIcon />} onClick={onSaveRsvp}>
                    Save
                  </Button>
                </Box>
              </Stack>
            </Paper>
          )}
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>Practice Plan Inputs</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' }, gap: 1.5 }}>
            <TextField size="small" label="Courts" type="number" value={settings.courts} inputProps={{ min: 1, max: 10 }} onChange={(change) => updateCourtCount(Number(change.target.value))} disabled={readOnly} />
            <TextField size="small" label="Total Minutes" type="number" value={settings.durationMinutes} inputProps={{ min: 30, max: 180 }} onChange={(change) => updateSettings({ durationMinutes: Number(change.target.value) })} disabled={readOnly} />
            <TextField size="small" label="Warmup Minutes" type="number" value={settings.warmupMinutes} inputProps={{ min: 0, max: 60 }} onChange={(change) => updateSettings({ warmupMinutes: Number(change.target.value) })} disabled={readOnly} helperText="Subtracted from total time before rotations." />
            <TextField size="small" label="Round Minutes" type="number" value={settings.roundDurationMinutes === 'auto' ? '' : settings.roundDurationMinutes} inputProps={{ min: 5, max: 60 }} onChange={(change) => updateSettings({ roundDurationMinutes: Number(change.target.value) || defaultPracticeRotationSettings.roundDurationMinutes })} disabled={readOnly} helperText="Enter the length of each rotation round." />
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(220px, 0.35fr) minmax(0, 1fr)' }, gap: 1.5 }}>
            <FormControl size="small" disabled={readOnly}>
              <InputLabel>Strength Mix</InputLabel>
              <Select
                label="Strength Mix"
                value={settings.strengthMix}
                onChange={(change) => updateSettings({ strengthMix: change.target.value as PracticeRotationSettings['strengthMix'] })}
              >
                <MenuItem value="balanced">Balanced</MenuItem>
                <MenuItem value="mixed">Mixed</MenuItem>
                <MenuItem value="development">Development</MenuItem>
                <MenuItem value="random">Random</MenuItem>
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                {practiceStrengthMixDescriptions[settings.strengthMix]}
              </Typography>
            </FormControl>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 800, mb: 0.75 }}>Court Labels</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {resizePracticeCourtLabels(settings.courtLabels, settings.courts).map((label, index) => (
                  <TextField
                    key={`court-label-${index}`}
                    size="small"
                    label={`Court ${index + 1}`}
                    value={label}
                    onChange={(change) => updateCourtLabel(index, change.target.value)}
                    disabled={readOnly}
                    sx={{ width: { xs: '100%', sm: 140 } }}
                  />
                ))}
              </Stack>
            </Box>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(220px, 1fr))' }, gap: 1.5 }}>
            <FormControl size="small" disabled={readOnly}>
              <InputLabel>Sit First Round</InputLabel>
              <Select
                multiple
                label="Sit First Round"
                value={settings.sitFirstPlayerIds}
                renderValue={(selected) => `${selected.length} selected`}
                onChange={(change) => updateSettings({ sitFirstPlayerIds: change.target.value as string[] })}
              >
                {matchedPlayers.map((participant) => <MenuItem key={participant.playerId || participant.id} value={participant.playerId || ''}>{participant.name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" disabled={readOnly}>
              <InputLabel>Do Not Sit</InputLabel>
              <Select
                multiple
                label="Do Not Sit"
                value={settings.noSitPlayerIds}
                renderValue={(selected) => `${selected.length} selected`}
                onChange={(change) => updateSettings({ noSitPlayerIds: change.target.value as string[] })}
              >
                {matchedPlayers.map((participant) => <MenuItem key={participant.playerId || participant.id} value={participant.playerId || ''}>{participant.name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" disabled={readOnly}>
              <InputLabel>Last To Sit</InputLabel>
              <Select
                multiple
                label="Last To Sit"
                value={settings.sitLastPlayerIds}
                renderValue={(selected) => `${selected.length} selected`}
                onChange={(change) => updateSettings({ sitLastPlayerIds: change.target.value as string[] })}
              >
                {matchedPlayers.map((participant) => <MenuItem key={participant.playerId || participant.id} value={participant.playerId || ''}>{participant.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <FormControlLabelLite label="Prepare upcoming match pairings" checked={settings.prepareUpcomingMatchPairings} onChange={(checked) => updateSettings({ prepareUpcomingMatchPairings: checked })} disabled={readOnly} />
            <FormControlLabelLite label="Use partner preferences" checked={settings.usePartnerPreferences} onChange={(checked) => updateSettings({ usePartnerPreferences: checked })} disabled={readOnly} />
            <FormControlLabelLite label="Prefer mixed teams" checked={settings.preferMixedTeams} onChange={(checked) => updateSettings({ preferMixedTeams: checked })} disabled={readOnly} />
          </Stack>

          <Divider />
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Captain Constraints</Typography>
          <ConstraintRows
            title="Arrive Late"
            rows={settings.arriveLate}
            players={matchedPlayers}
            onChange={(rows) => updateSettings({ arriveLate: rows })}
            disabled={readOnly}
          />
          <ConstraintRows
            title="Leave Early"
            rows={settings.leaveEarly}
            players={matchedPlayers}
            onChange={(rows) => updateSettings({ leaveEarly: rows })}
            disabled={readOnly}
          />
          <PairRows
            title="Must Partner"
            rows={settings.mustPartner}
            players={matchedPlayers}
            onAdd={() => addPairRule('mustPartner')}
            onChange={(rows) => updateSettings({ mustPartner: rows })}
            disabled={readOnly}
          />
          <PairRows
            title="Avoid Partner"
            rows={settings.avoidPartner}
            players={matchedPlayers}
            onAdd={() => addPairRule('avoidPartner')}
            onChange={(rows) => updateSettings({ avoidPartner: rows })}
            disabled={readOnly}
          />

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button variant="contained" startIcon={<AutoAwesomeIcon />} disabled={readOnly || busy || loading} onClick={() => void generate()}>
              {activePlan ? 'Regenerate' : 'Generate Practice Plan'}
            </Button>
            {activePlan && (
              <Button variant="outlined" startIcon={<RefreshIcon />} disabled={readOnly || busy || loading} onClick={() => void generate()}>
                Refresh Rotation
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>

      {activePlanCandidate && !activePlanValidation.valid && (
        <Alert severity="error">
          CourtCaptain couldn't produce a valid rotation. Please regenerate the plan.
        </Alert>
      )}

      {activePlan && (
        <PracticePlanView
          plan={activePlan}
          staleDifferences={staleDifferences}
          onCopy={() => void copyPlan()}
          onPdf={() => void downloadPdf()}
        />
      )}
    </Stack>
  );
}

function FormControlLabelLite({ label, checked, onChange, disabled }: { label: string; checked: boolean; onChange: (checked: boolean) => void; disabled: boolean }) {
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Checkbox size="small" checked={checked} onChange={(event) => onChange(event.target.checked)} disabled={disabled} />
      <Typography variant="body2">{label}</Typography>
    </Stack>
  );
}

function ConstraintRows({
  title,
  rows,
  players,
  onChange,
  disabled,
}: {
  title: string;
  rows: { playerId: string; round: number }[];
  players: PracticeRotationParticipant[];
  onChange: (rows: { playerId: string; round: number }[]) => void;
  disabled: boolean;
}) {
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="body2" sx={{ fontWeight: 800 }}>{title}</Typography>
        <Button size="small" disabled={disabled || players.length === 0} onClick={() => onChange([...rows, { playerId: players[0].playerId || '', round: 2 }])}>Add</Button>
      </Stack>
      <Stack spacing={1} sx={{ mt: rows.length ? 1 : 0 }}>
        {rows.map((row, index) => (
          <Stack key={`${title}:${index}`} direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <FormControl size="small" sx={{ minWidth: 220 }} disabled={disabled}>
              <InputLabel>Player</InputLabel>
              <Select label="Player" value={row.playerId} onChange={(change) => onChange(rows.map((item, rowIndex) => rowIndex === index ? { ...item, playerId: change.target.value } : item))}>
                {players.map((player) => <MenuItem key={player.playerId || player.id} value={player.playerId || ''}>{player.name}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField size="small" label="Round" type="number" value={row.round} inputProps={{ min: 1 }} disabled={disabled} onChange={(change) => onChange(rows.map((item, rowIndex) => rowIndex === index ? { ...item, round: Number(change.target.value) } : item))} />
            <Button color="error" disabled={disabled} onClick={() => onChange(rows.filter((_, rowIndex) => rowIndex !== index))}>Remove</Button>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

function PairRows({
  title,
  rows,
  players,
  onAdd,
  onChange,
  disabled,
}: {
  title: string;
  rows: PracticeRotationPairRule[];
  players: PracticeRotationParticipant[];
  onAdd: () => void;
  onChange: (rows: PracticeRotationPairRule[]) => void;
  disabled: boolean;
}) {
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="body2" sx={{ fontWeight: 800 }}>{title}</Typography>
        <Button size="small" disabled={disabled || players.length < 2} onClick={onAdd}>Add</Button>
      </Stack>
      <Stack spacing={1} sx={{ mt: rows.length ? 1 : 0 }}>
        {rows.map((row, index) => (
          <Stack key={`${title}:${index}`} direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            {(['player1Id', 'player2Id'] as const).map((field) => (
              <FormControl key={field} size="small" sx={{ minWidth: 220 }} disabled={disabled}>
                <InputLabel>{field === 'player1Id' ? 'Player 1' : 'Player 2'}</InputLabel>
                <Select label={field === 'player1Id' ? 'Player 1' : 'Player 2'} value={row[field]} onChange={(change) => onChange(rows.map((item, rowIndex) => rowIndex === index ? { ...item, [field]: change.target.value } : item))}>
                  {players.map((player) => <MenuItem key={player.playerId || player.id} value={player.playerId || ''}>{player.name}</MenuItem>)}
                </Select>
              </FormControl>
            ))}
            <Button color="error" disabled={disabled} onClick={() => onChange(rows.filter((_, rowIndex) => rowIndex !== index))}>Remove</Button>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

function PracticePlanView({ plan, staleDifferences, onCopy, onPdf }: { plan: PracticeRotationPlan; staleDifferences: string[]; onCopy: () => void; onPdf: () => void }) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>Current Practice Rotation</Typography>
            <Typography variant="body2" color="text.secondary">
              Generated {formatGeneratedAt(plan.generatedAt)} · Quality {plan.metrics.qualityScore}/100 · {plan.metrics.roundCount} rounds · {plan.metrics.courtUtilizationPercent}% court utilization
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Warmup {plan.metrics.warmupMinutes} min · Rotation window {plan.metrics.rotationMinutes} min · Each round needs {plan.metrics.roundDurationMinutes} min based on {plan.metrics.participantCount} people and {plan.metrics.courts} court{plan.metrics.courts === 1 ? '' : 's'}.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={onCopy}>Copy as Text</Button>
            <Button variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={onPdf}>Generate PDF</Button>
          </Stack>
        </Stack>

        {staleDifferences.length > 0 && (
          <Alert severity="warning">
            Current RSVPs differ from the saved plan: {staleDifferences.join('; ')}. Use Refresh Rotation to regenerate from the latest Going list.
          </Alert>
        )}
        {plan.warnings.map((warning) => <Alert key={warning} severity="info">{warning}</Alert>)}

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' }, gap: 1 }}>
          <Metric label="Playing time" value={`${plan.metrics.playCountMin}-${plan.metrics.playCountMax} rounds`} />
          <Metric label="Sit-outs" value={`${plan.metrics.sitCountMin}-${plan.metrics.sitCountMax} rounds`} />
          <Metric label="Round length" value={`${plan.metrics.roundDurationMinutes} min`} />
          <Metric label="Mixed teams" value={`${plan.metrics.mixedTeamPercent}%`} />
          <Metric label="Upcoming pairs" value={`${plan.metrics.upcomingPairsPracticed}/${plan.metrics.upcomingPairsAvailable}`} />
        </Box>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Round</TableCell>
              <TableCell>Sitting</TableCell>
              {resizePracticeCourtLabels(plan.settingsSnapshot.courtLabels, plan.metrics.courts).map((label, index) => <TableCell key={index}>{displayPracticeCourtLabel(label)}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {plan.rounds.map((round) => (
              <TableRow key={round.roundNumber}>
                <TableCell sx={{ fontWeight: 800 }}>
                  {round.roundNumber}
                  <Typography variant="caption" display="block" color="text.secondary">
                    {round.startMinute}-{round.endMinute} min · {round.endMinute - round.startMinute} min
                  </Typography>
                </TableCell>
                <TableCell>{round.sitting.length ? round.sitting.map((player) => player.name).join(', ') : '—'}</TableCell>
                {round.courts.map((court) => (
                  <TableCell key={court.courtNumber}>
                    {teamText(court.teamA)} vs {teamText(court.teamB)}
                    {court.note ? <Typography variant="caption" display="block" color="text.secondary">{court.note}</Typography> : null}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Typography variant="caption" color="text.secondary">★ means CourtCaptain recognized an upcoming saved match partnership and tried to give that pair reps together.</Typography>
      </Stack>
    </Paper>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.25 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h6" sx={{ fontWeight: 800 }}>{value}</Typography>
    </Paper>
  );
}
